import { APIHandler } from "../../apiHandler";
import { getContext } from "../../context";

class LspHandler extends APIHandler {
  async get() {
    const proxyUrl = getContext().jupyterProxyUrl;
    if (proxyUrl) {
      // If it's possible to proxy this request, do it.
      await this.proxyRequest();
      return;
    }

    if (this.request.url.includes("/lsp/status")) {
      // If this is a status request but we're not actually
      // connected to a backend, return some fake data.
      this.finish({
        version: 2,
        sessions: {
          "pylsp": {
            handler_count: 0,
            status: "stopped",
            last_server_message_at: "2022-01-27T12:53:54.029717+00:00",
            last_handler_message_at: "2022-01-27T12:53:53.444798+00:00",
            spec: {
              languages: ["python"],
              version: 2,
              display_name: "python-lsp-server (pylsp)",
              mime_types: ["text/python", "text/x-ipython"],
              urls: {
                home: "https://github.com/python-lsp/python-lsp-server",
                issues:
                  "https://github.com/python-lsp/python-lsp-server/issues",
              },
              install: {
                pip: "pip install 'python-lsp-server[all]'",
                conda: "conda install -c conda-forge python-lsp-server",
              },
              extend: [
                {
                  display_name: "pyls-mypy",
                  install: {
                    pip: "pip install pyls-mypy",
                    conda: "conda install pyls-mypy",
                  },
                },
                {
                  display_name: "pyls-black",
                  install: {
                    pip: "pip install pyls-black",
                    conda: "conda install pyls-black",
                  },
                },
                {
                  display_name: "pyls-isort",
                  install: {
                    pip: "pip install pyls-isort",
                    conda: "conda install pyls-isort",
                  },
                },
                {
                  display_name: "pyls-memestra",
                  install: {
                    pip: "pip install pyls-memestra",
                    conda: "conda install pyls-memestra",
                  },
                },
              ],
              config_schema: {
                title: "Python Language Server Configuration",
                type: "object",
                properties: {
                  "pylsp.executable": {
                    type: "string",
                    default: "pylsp",
                    description: "Language server executable",
                  },
                  "pylsp.configurationSources": {
                    type: "array",
                    default: ["pycodestyle"],
                    description: "List of configuration sources to use.",
                    items: {
                      type: "string",
                      enum: ["pycodestyle", "pyflakes"],
                    },
                    uniqueItems: true,
                  },
                  "pylsp.plugins.jedi_completion.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_completion.include_params": {
                    type: "boolean",
                    default: true,
                    description:
                      "Auto-completes methods and classes with tabstops for each parameter.",
                  },
                  "pylsp.plugins.jedi_definition.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_definition.follow_imports": {
                    type: "boolean",
                    default: true,
                    description: "The goto call will follow imports.",
                  },
                  "pylsp.plugins.jedi_definition.follow_builtin_imports": {
                    type: "boolean",
                    default: true,
                    description:
                      "If follow_imports is True will decide if it follow builtin imports.",
                  },
                  "pylsp.plugins.jedi_hover.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_references.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_signature_help.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_symbols.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.jedi_symbols.all_scopes": {
                    type: "boolean",
                    default: true,
                    description:
                      "If True lists the names of all scopes instead of only the module namespace.",
                  },
                  "pylsp.plugins.mccabe.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.mccabe.threshold": {
                    type: "number",
                    default: 15,
                    description:
                      "The minimum threshold that triggers warnings about cyclomatic complexity.",
                  },
                  "pylsp.plugins.preload.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.preload.modules": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description: "List of modules to import on startup",
                  },
                  "pylsp.plugins.pycodestyle.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.pycodestyle.exclude": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description:
                      "Exclude files or directories which match these patterns.",
                  },
                  "pylsp.plugins.pycodestyle.filename": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description:
                      "When parsing directories, only check filenames matching these patterns.",
                  },
                  "pylsp.plugins.pycodestyle.select": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description: "Select errors and warnings",
                  },
                  "pylsp.plugins.pycodestyle.ignore": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description: "Ignore errors and warnings",
                  },
                  "pylsp.plugins.pycodestyle.hangClosing": {
                    type: "boolean",
                    default: null,
                    description:
                      "Hang closing bracket instead of matching indentation of opening bracket's line.",
                  },
                  "pylsp.plugins.pycodestyle.maxLineLength": {
                    type: "number",
                    default: null,
                    description: "Set maximum allowed line length.",
                  },
                  "pylsp.plugins.pydocstyle.enabled": {
                    type: "boolean",
                    default: false,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.pydocstyle.convention": {
                    type: "string",
                    default: null,
                    enum: ["pep257", "numpy"],
                    description:
                      "Choose the basic list of checked errors by specifying an existing convention.",
                  },
                  "pylsp.plugins.pydocstyle.addIgnore": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description:
                      "Ignore errors and warnings in addition to the specified convention.",
                  },
                  "pylsp.plugins.pydocstyle.addSelect": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description:
                      "Select errors and warnings in addition to the specified convention.",
                  },
                  "pylsp.plugins.pydocstyle.ignore": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description: "Ignore errors and warnings",
                  },
                  "pylsp.plugins.pydocstyle.select": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description: "Select errors and warnings",
                  },
                  "pylsp.plugins.pydocstyle.match": {
                    type: "string",
                    default: "(?!test_).*\\.py",
                    description:
                      "Check only files that exactly match the given regular expression; default is to match files that don't start with 'test_' but end with '.py'.",
                  },
                  "pylsp.plugins.pydocstyle.matchDir": {
                    type: "string",
                    default: "[^\\.].*",
                    description:
                      "Search only dirs that exactly match the given regular expression; default is to match dirs which do not begin with a dot.",
                  },
                  "pylsp.plugins.pyflakes.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.pylint.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.pylint.args": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: false,
                    description: "Arguments to pass to pylint.",
                  },
                  "pylsp.plugins.rope_completion.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.plugins.yapf.enabled": {
                    type: "boolean",
                    default: true,
                    description: "Enable or disable the plugin.",
                  },
                  "pylsp.rope.extensionModules": {
                    type: "string",
                    default: null,
                    description:
                      "Builtin and c-extension modules that are allowed to be imported and inspected by rope.",
                  },
                  "pylsp.rope.ropeFolder": {
                    type: "array",
                    default: null,
                    items: { type: "string" },
                    uniqueItems: true,
                    description:
                      "The name of the folder in which rope stores project configurations and data.  Pass `null` for not using such a folder at all.",
                  },
                },
              },
            },
          },
          "r-languageserver": {
            handler_count: 0,
            status: "not_started",
            last_server_message_at: null,
            last_handler_message_at: null,
            spec: {
              languages: ["r"],
              version: 2,
              display_name: "r-languageserver",
              mime_types: ["text/x-rsrc"],
              urls: {
                home: "https://github.com/REditorSupport/languageserver",
                issues:
                  "https://github.com/REditorSupport/languageserver/issues",
              },
              install: {
                cran: 'install.packages("languageserver")',
                conda: "conda install -c conda-forge r-languageserver",
              },
            },
          },
        },
      });
      return;
    }

    // No backend, just return nothing.
    this.finish({});
  }

  async post() {
    await this.proxyRequest();
  }
}

export const defaultHandlers: [string, typeof APIHandler][] = [
  ["/lsp/status", LspHandler],
  ["/lab/api/settings/@krassowski/jupyterlab-lsp:plugin", LspHandler],
  ["/lsp/ws/pylsp", LspHandler],
  ["/lsp/ws/r-languageserver", LspHandler],
];
